/**
 * VBRecommendationPanel.tsx
 *
 * VB Recommendation two-step voting interface:
 * Step 1: Team Vote (5 voters, PROMOTE/REJECT only)
 * Step 2: Leadership Approval (CEO + COO, both must PROMOTE)
 *
 * Props:
 * - projectId: number — project to fetch and manage voting for
 *
 * API Interactions:
 * - GET /api/v2/projects/:id/vote-rounds — fetch all rounds
 * - POST /api/v2/projects/:id/vote-rounds — create new VB rounds
 * - POST /api/v2/vote-rounds/:roundId/votes — cast a vote
 * - PATCH /api/v2/vote-rounds/:roundId/close — close a round
 */

import { useEffect, useState } from 'react'
import { authFetchJson } from '../../lib/authFetch'
import { InlineNote } from '../ui/InlineNote'
import type { LifecycleProjectVote, VoteValue } from '../../types'

interface VBRecommendationPanelProps {
  projectId: number
}

interface VoteRoundWithVotes {
  id: number
  project_id: number
  round_type: string
  status: string
  created_by: number | null
  created_at: string
  closed_at: string | null
  result: string | null
  votes?: LifecycleProjectVote[]
}

interface ApiRoundsResponse {
  rounds: VoteRoundWithVotes[]
}

interface ApiRoundCreateResponse {
  round: VoteRoundWithVotes
  message?: string
}

interface ApiVoteResponse {
  vote: LifecycleProjectVote
  message?: string
}

interface ApiCloseRoundResponse {
  round: VoteRoundWithVotes
  result?: string
  message?: string
}

// Team voters (same as IDA Review)
const TEAM_VOTERS = [
  { name: 'Venture Architect 1', role: 'VA · Lead' },
  { name: 'Venture Architect 2', role: 'VA' },
  { name: 'Venture Architect 3', role: 'VA' },
  { name: 'Venture Architect 4', role: 'VA' },
  { name: 'Director of R&D Collaboration', role: 'Director' },
]

// Leadership voters (CEO + COO)
const LEADERSHIP_VOTERS = [
  { name: 'Maciej Marszałek', role: 'CEO', isLeadership: true },
  { name: 'Jędrzej Iwaszkiewicz', role: 'COO', isLeadership: true },
]

const VOTE_BADGE_COLORS: Record<VoteValue, string> = {
  'PROMOTE': 'bg-[#10B981] text-white',
  'HOLD': 'bg-[#F59E0B] text-white',
  'REJECT': 'bg-[#E61B25] text-white',
}

const APPROVED_BADGE_COLOR = 'bg-[#10B981] text-white'
const REJECTED_BADGE_COLOR = 'bg-[#E61B25] text-white'
const PENDING_BADGE_COLOR = 'bg-[#4a4a6a] text-white'

export function VBRecommendationPanel({ projectId }: VBRecommendationPanelProps) {
  const [rounds, setRounds] = useState<VoteRoundWithVotes[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [castingVote, setCastingVote] = useState<{
    roundType: 'VB_RECOMMENDATION_TEAM' | 'VB_RECOMMENDATION_EXEC'
    voterName: string
    vote: VoteValue | ''
    comment: string
  } | null>(null)
  const [savingVote, setSavingVote] = useState(false)
  const [recommendation, setRecommendation] = useState('')
  const [savingRecommendation, setSavingRecommendation] = useState(false)

  // Fetch voting rounds on mount
  useEffect(() => {
    const fetchRounds = async () => {
      try {
        setLoading(true)
        const data = await authFetchJson<ApiRoundsResponse>(
          `/api/v2/projects/${projectId}/vote-rounds`
        )
        setRounds(data.rounds)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch voting rounds')
      } finally {
        setLoading(false)
      }
    }

    fetchRounds()
  }, [projectId])

  // Get rounds
  const teamRound = rounds.find(r => r.round_type === 'VB_RECOMMENDATION_TEAM')
  const execRound = rounds.find(r => r.round_type === 'VB_RECOMMENDATION_EXEC')

  const createTeamRound = async () => {
    try {
      const data = await authFetchJson<ApiRoundCreateResponse>(
        `/api/v2/projects/${projectId}/vote-rounds`,
        {
          method: 'POST',
          json: { round_type: 'VB_RECOMMENDATION_TEAM' },
        }
      )
      setRounds(prev => [data.round, ...prev])
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team voting round')
    }
  }

  const createExecRound = async () => {
    try {
      const data = await authFetchJson<ApiRoundCreateResponse>(
        `/api/v2/projects/${projectId}/vote-rounds`,
        {
          method: 'POST',
          json: { round_type: 'VB_RECOMMENDATION_EXEC' },
        }
      )
      setRounds(prev => [data.round, ...prev])
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create leadership voting round')
    }
  }

  const castVote = async (
    roundType: 'VB_RECOMMENDATION_TEAM' | 'VB_RECOMMENDATION_EXEC',
    voterName: string,
    vote: VoteValue | 'APPROVED',
    comment: string
  ) => {
    const round = roundType === 'VB_RECOMMENDATION_TEAM' ? teamRound : execRound
    if (!round) return

    try {
      setSavingVote(true)
      const data = await authFetchJson<ApiVoteResponse>(
        `/api/v2/vote-rounds/${round.id}/votes`,
        {
          method: 'POST',
          json: {
            voter_name: voterName,
            voter_role: roundType === 'VB_RECOMMENDATION_EXEC' ? 'Leadership' : 'Venture Architect',
            vote: vote as VoteValue,
            comment: comment || undefined,
          },
        }
      )

      // Update the round with the new vote
      setRounds(prev =>
        prev.map(r =>
          r.id === round.id
            ? { ...r, votes: [...(r.votes || []), data.vote] }
            : r
        )
      )
      setCastingVote(null)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cast vote')
    } finally {
      setSavingVote(false)
    }
  }

  const closeRound = async (roundType: 'VB_RECOMMENDATION_TEAM' | 'VB_RECOMMENDATION_EXEC') => {
    const round = roundType === 'VB_RECOMMENDATION_TEAM' ? teamRound : execRound
    if (!round) return

    try {
      setSavingVote(true)
      const data = await authFetchJson<ApiCloseRoundResponse>(
        `/api/v2/vote-rounds/${round.id}/close`,
        { method: 'PATCH' }
      )

      // Update the round status and result
      setRounds(prev =>
        prev.map(r =>
          r.id === round.id
            ? {
              ...data.round,
              votes: data.round.votes || r.votes || []
            }
            : r
        )
      )
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close voting round')
    } finally {
      setSavingVote(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-4 bg-[#2a2a44] rounded w-1/3 animate-pulse" />
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-[#2a2a44] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 py-3 bg-[#E61B25]/10 border border-[#E61B25]/30 rounded-lg text-[#E61B25] text-sm">
        {error}
      </div>
    )
  }

  // Determine voting readiness
  const teamVotesReady = teamRound && teamRound.status === 'CLOSED'

  return (
    <div className="space-y-8">
      {/* Recommendation Document Section */}
      <div className="space-y-4">
        <h3 className="text-[16px] font-semibold text-[#e8e8f0]">Recommendation Document</h3>

        {/* Key Strengths & Risks */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4">
            <h4 className="text-[13px] font-semibold text-[#10B981] mb-3">Key Strengths</h4>
            <p className="text-[12px] text-[#a8a8c0]">
              Strong team, clear IP strategy, validated market demand
            </p>
          </div>
          <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4">
            <h4 className="text-[13px] font-semibold text-[#E61B25] mb-3">Key Risks</h4>
            <p className="text-[12px] text-[#a8a8c0]">
              Regulatory complexity, limited manufacturing partnerships
            </p>
          </div>
        </div>

        {/* Summary Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4">
            <p className="text-[11px] text-[#a8a8c0] mb-2">IRL Average</p>
            <p className="text-[20px] font-semibold text-[#e8e8f0]">7.2</p>
          </div>
          <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4">
            <p className="text-[11px] text-[#a8a8c0] mb-2">Scoring</p>
            <p className="text-[20px] font-semibold text-[#e8e8f0]">8.1</p>
          </div>
          <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4">
            <p className="text-[11px] text-[#a8a8c0] mb-2">Moat Strength</p>
            <p className="text-[20px] font-semibold text-[#e8e8f0]">6.8</p>
          </div>
          <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4">
            <p className="text-[11px] text-[#a8a8c0] mb-2">Team Assessment</p>
            <p className="text-[20px] font-semibold text-[#e8e8f0]">8.5</p>
          </div>
        </div>

        {/* VA Recommendation */}
        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4">
          <h4 className="text-[13px] font-semibold text-[#e8e8f0] mb-3">VA Recommendation</h4>
          <InlineNote
            value={recommendation}
            onChange={setRecommendation}
            placeholder="Add recommendation from venture architect..."
            variant="va"
            authorName="Sarah Chen"
            date="2026-04-04"
            editable={true}
          />
        </div>
      </div>

      {/* Step 1 — Team Vote */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-[#F59E0B] flex items-center justify-center text-white text-[12px] font-bold">
            1
          </div>
          <h3 className="text-[16px] font-semibold text-[#e8e8f0]">Team Vote</h3>
          {teamRound && (
            <span className={`${teamRound.status === 'CLOSED' ? 'bg-[#10B981]' : 'bg-[#F59E0B]'} text-white text-[10px] font-semibold px-2 py-1 rounded-full`}>
              {teamRound.status === 'CLOSED' ? 'COMPLETED' : 'OPEN'}
            </span>
          )}
        </div>

        {!teamRound ? (
          <button
            onClick={createTeamRound}
            className="px-4 py-2 bg-[#F59E0B] text-white rounded-lg text-sm font-medium hover:bg-[#e59100] transition-colors"
          >
            Start Team Voting
          </button>
        ) : (
          <VoterStep
            round={teamRound}
            voters={TEAM_VOTERS}
            onCastVote={(voterName, vote, comment) => {
              castVote('VB_RECOMMENDATION_TEAM', voterName, vote as VoteValue, comment)
            }}
            onClose={() => closeRound('VB_RECOMMENDATION_TEAM')}
            onVoteClick={(voter) => {
              if (teamRound.status === 'OPEN' && !voter.vote) {
                setCastingVote({
                  roundType: 'VB_RECOMMENDATION_TEAM',
                  voterName: voter.name,
                  vote: '',
                  comment: '',
                })
              }
            }}
            casting={castingVote?.roundType === 'VB_RECOMMENDATION_TEAM' ? castingVote : null}
            castingVoter={castingVote?.roundType === 'VB_RECOMMENDATION_TEAM' ? castingVote.voterName : null}
            saving={savingVote}
            requiresApproval={false}
            onUpdateCasting={setCastingVote}
          />
        )}
      </div>

      {/* Step 2 — Leadership Approval */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-[#E61B25] flex items-center justify-center text-white text-[12px] font-bold">
            2
          </div>
          <h3 className="text-[16px] font-semibold text-[#e8e8f0]">Leadership Approval</h3>
          {execRound && (
            <span className={`${execRound.status === 'CLOSED' ? 'bg-[#10B981]' : 'bg-[#E61B25]'} text-white text-[10px] font-semibold px-2 py-1 rounded-full`}>
              {execRound.status === 'CLOSED' ? 'COMPLETED' : 'OPEN'}
            </span>
          )}
        </div>

        {teamVotesReady ? (
          !execRound ? (
            <button
              onClick={createExecRound}
              className="px-4 py-2 bg-[#E61B25] text-white rounded-lg text-sm font-medium hover:bg-[#d41620] transition-colors"
            >
              Start Leadership Approval
            </button>
          ) : (
            <VoterStep
              round={execRound}
              voters={LEADERSHIP_VOTERS}
              onCastVote={(voterName, vote, comment) => {
                // Convert PROMOTE to APPROVED for leadership
                castVote('VB_RECOMMENDATION_EXEC', voterName, 'PROMOTE', comment)
              }}
              onClose={() => closeRound('VB_RECOMMENDATION_EXEC')}
              onVoteClick={(voter) => {
                if (execRound.status === 'OPEN' && !voter.vote) {
                  setCastingVote({
                    roundType: 'VB_RECOMMENDATION_EXEC',
                    voterName: voter.name,
                    vote: '',
                    comment: '',
                  })
                }
              }}
              casting={castingVote?.roundType === 'VB_RECOMMENDATION_EXEC' ? castingVote : null}
              castingVoter={castingVote?.roundType === 'VB_RECOMMENDATION_EXEC' ? castingVote.voterName : null}
              saving={savingVote}
              requiresApproval={true}
              onUpdateCasting={setCastingVote}
              isLeadership={true}
            />
          )
        ) : (
          <div className="bg-[#2a2a44] rounded-lg p-4 text-[12px] text-[#a8a8c0]">
            Complete team voting before starting leadership approval.
          </div>
        )}
      </div>

      {/* Final Result */}
      {execRound?.status === 'CLOSED' && (execRound.result as string) === 'APPROVED' && (
        <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#10B981]">
            <span className="text-[20px]">✓</span>
            <span className="text-[16px] font-semibold">Decision: APPROVED for VB Process</span>
          </div>
          <button className="w-full px-4 py-3 bg-[#10B981] text-white rounded-lg font-medium hover:bg-[#0a9970] transition-colors">
            Start VB Process →
          </button>
        </div>
      )}
    </div>
  )
}

interface VoterStepProps {
  round: VoteRoundWithVotes
  voters: Array<{ name: string; role: string; isLeadership?: boolean }>
  onCastVote: (voterName: string, vote: VoteValue | 'APPROVED', comment: string) => void
  onClose: () => void
  onVoteClick: (voter: { name: string; vote?: LifecycleProjectVote | null }) => void
  casting: { roundType: string; voterName: string; vote: VoteValue | ''; comment: string } | null
  castingVoter: string | null
  saving: boolean
  requiresApproval?: boolean
  onUpdateCasting: (state: any) => void
  isLeadership?: boolean
}

function VoterStep({
  round,
  voters,
  onCastVote,
  onClose,
  onVoteClick,
  casting,
  castingVoter,
  saving,
  requiresApproval,
  onUpdateCasting,
  isLeadership,
}: VoterStepProps) {
  const votes = round.votes || []
  const votesByName: Record<string, LifecycleProjectVote> = Object.fromEntries(
    votes.map(v => [v.voter_name, v])
  )

  const votersWithVotes = voters.map(voter => ({
    ...voter,
    vote: votesByName[voter.name] || null,
  }))

  const promoteCount = votes.filter(v => (v.vote as string) === 'PROMOTE').length
  const rejectCount = votes.filter(v => (v.vote as string) === 'REJECT').length
  const totalVotes = votes.length
  const hasMajority = totalVotes > 0 && promoteCount > totalVotes / 2
  const allApproved = isLeadership && totalVotes === voters.length && votes.every(v => (v.vote as string) === 'PROMOTE')

  const shouldShowClose = round.status === 'OPEN' && (
    requiresApproval ? allApproved : hasMajority
  )

  return (
    <div className="space-y-4">
      {/* Voter Cards */}
      <div className="space-y-3">
        {votersWithVotes.map(voter => {
          const hasVoted = !!voter.vote
          const initials = voter.name.split(' ').map(w => w[0]).join('').toUpperCase()
          const isLeadershipVoter = isLeadership || voter.isLeadership

          return (
            <div
              key={voter.name}
              onClick={() => {
                if (round.status === 'OPEN' && !hasVoted) {
                  onVoteClick(voter)
                }
              }}
              className={`
                bg-[#1e1e38] border rounded-xl p-4
                flex items-start gap-4
                transition-colors
                ${isLeadershipVoter ? 'border-[#E61B25] border-2' : 'border-[#2a2a44]'}
                ${round.status === 'OPEN' && !hasVoted ? 'cursor-pointer hover:border-[#3a3a54]' : ''}
              `}
            >
              {/* Avatar */}
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                ${isLeadershipVoter
                  ? 'bg-gradient-to-br from-[#E61B25] to-[#d41620]'
                  : 'bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]'
                }
              `}>
                <span className="text-white text-[12px] font-semibold">{initials}</span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium text-[#e8e8f0]">{voter.name}</span>
                  <span className="text-[11px] text-[#a8a8c0]">{voter.role}</span>
                </div>

                {voter.vote?.comment && (
                  <p className="text-[12px] text-[#a8a8c0] italic mt-2">{voter.vote.comment}</p>
                )}
              </div>

              {/* Vote Badge */}
              <div className="flex-shrink-0">
                {voter.vote ? (
                  <div className={`px-3 py-1 rounded-full text-[11px] font-semibold ${VOTE_BADGE_COLORS[voter.vote.vote as VoteValue] || APPROVED_BADGE_COLOR}`}>
                    {voter.vote.vote}
                  </div>
                ) : (
                  <div className={`px-3 py-1 rounded-full text-[11px] font-semibold ${PENDING_BADGE_COLOR}`}>
                    PENDING
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Vote Dialog */}
      {casting && castingVoter === casting.voterName && round.status === 'OPEN' && (
        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4 space-y-4">
          <div>
            <label className="text-[12px] font-medium text-[#a8a8c0] block mb-2">
              Vote
            </label>
            <div className="flex gap-2">
              {['PROMOTE', 'REJECT'].map(option => (
                <button
                  key={option}
                  onClick={() => onUpdateCasting({ ...casting, vote: option as VoteValue })}
                  className={`
                    flex-1 px-3 py-2 rounded-lg text-[12px] font-medium transition-colors
                    ${casting.vote === option
                      ? `${option === 'PROMOTE' ? 'bg-[#10B981]' : 'bg-[#E61B25]'} text-white border border-transparent`
                      : 'bg-[#2a2a44] text-[#a8a8c0] border border-[#3a3a54] hover:border-[#4a4a6a]'
                    }
                  `}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[12px] font-medium text-[#a8a8c0] block mb-2">
              Comment (optional)
            </label>
            <textarea
              value={casting.comment}
              onChange={e => onUpdateCasting({ ...casting, comment: e.target.value })}
              placeholder="Add a comment..."
              className="w-full px-3 py-2 bg-[#12122a] border border-[#2a2a44] rounded-lg text-[13px] text-[#e8e8f0] placeholder-[#4a4a6a] focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50 resize-none"
              rows={3}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => onUpdateCasting(null)}
              disabled={saving}
              className="px-3 py-2 border border-[#2a2a44] rounded-lg text-[12px] font-medium text-[#a8a8c0] hover:border-[#3a3a54] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (casting.vote) {
                  onCastVote(casting.voterName, casting.vote as VoteValue, casting.comment)
                }
              }}
              disabled={!casting.vote || saving}
              className="px-4 py-2 bg-[#E61B25] text-white rounded-lg text-[12px] font-medium hover:bg-[#d41620] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Submitting...' : 'Submit Vote'}
            </button>
          </div>
        </div>
      )}

      {/* Result Bar */}
      {totalVotes > 0 && (
        <div className="bg-[#2a2a44] rounded-lg p-4 space-y-3">
          <div className="text-[13px] text-[#a8a8c0]">
            {requiresApproval ? (
              <>
                <span className="font-medium text-[#10B981]">{promoteCount}</span> Approved
                {' '}· <span className="font-medium text-[#E61B25]">{rejectCount}</span> Rejected
              </>
            ) : (
              <>
                <span className="font-medium text-[#10B981]">{promoteCount}</span> Promote
                {' '}· <span className="font-medium text-[#E61B25]">{rejectCount}</span> Reject
              </>
            )}
          </div>

          {round.status === 'CLOSED' && round.result && (
            <div className="text-[12px] font-medium">
              Result:{' '}
              <span className={`
                ${['PROMOTE', 'APPROVED'].includes(round.result as string) ? 'text-[#10B981]' : 'text-[#E61B25]'}
              `}>
                {round.result}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Close Button */}
      {shouldShowClose && (
        <button
          onClick={onClose}
          disabled={saving}
          className={`
            w-full px-4 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed
            ${requiresApproval
              ? 'bg-[#E61B25] hover:bg-[#d41620]'
              : 'bg-[#10B981] hover:bg-[#0a9970]'
            }
          `}
        >
          {saving ? 'Closing...' : `Close ${requiresApproval ? 'Leadership' : 'Team'} Voting`}
        </button>
      )}
    </div>
  )
}

export default VBRecommendationPanel
